import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FullScreenLoader from '../components/common/AptiviewLoader/FullScreenLoader';

const LoadingContext = createContext(null);

const DEFAULT_MESSAGE = 'Getting your interview space ready…';

/**
 * Global content-area page loader (main panel only; sidebar stays visible).
 * Use usePageLoading(isLoading) on pages that fetch data.
 * Do not use for button/modal action spinners.
 */
export function LoadingProvider({ children }) {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const countRef = useRef(0);
  const messagesRef = useRef(new Map());
  const nextIdRef = useRef(0);

  const syncMessage = useCallback(() => {
    const values = [...messagesRef.current.values()];
    setMessage(values[values.length - 1] || DEFAULT_MESSAGE);
  }, []);

  const begin = useCallback((msg = DEFAULT_MESSAGE) => {
    const id = ++nextIdRef.current;
    messagesRef.current.set(id, msg || DEFAULT_MESSAGE);
    countRef.current += 1;

    if (countRef.current === 1) {
      setVisible(true);
    }
    syncMessage();
    return id;
  }, [syncMessage]);

  const end = useCallback((id) => {
    if (id != null && messagesRef.current.has(id)) {
      messagesRef.current.delete(id);
    }

    countRef.current = Math.max(0, countRef.current - 1);
    syncMessage();

    if (countRef.current > 0) return;

    setVisible(false);
    setMessage(DEFAULT_MESSAGE);
  }, [syncMessage]);

  const value = useMemo(() => ({ begin, end, visible }), [begin, end, visible]);

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <FullScreenLoader show={visible} message={message} />
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return ctx;
}

/**
 * Drive the global content-area loader from a page/data loading boolean.
 * @param {boolean} isLoading
 * @param {{ message?: string, enabled?: boolean }} [options]
 */
export function usePageLoading(isLoading, options = {}) {
  const { message = DEFAULT_MESSAGE, enabled = true } = options;
  const { begin, end } = useLoading();
  const requestIdRef = useRef(null);

  useEffect(() => {
    const shouldShow = Boolean(enabled && isLoading);

    if (shouldShow && requestIdRef.current == null) {
      requestIdRef.current = begin(message);
    }

    if (!shouldShow && requestIdRef.current != null) {
      end(requestIdRef.current);
      requestIdRef.current = null;
    }

    return () => {
      if (requestIdRef.current != null) {
        end(requestIdRef.current);
        requestIdRef.current = null;
      }
    };
  }, [isLoading, enabled, message, begin, end]);
}
